import {
  createReference,
  GroupSecurity,
  getReferenceData,
  TaskCode,
} from '@quromedical/fhir-common';
import { useNavigation } from '@react-navigation/native';
import type { PatientRouteParams } from 'app/routes';
import { WithGroup } from 'core/auth';
import { type CustomAction, PatientHeaderSection } from 'core/patient';
import {
  Assignment,
  type AssignmentResult,
  type NavigateToTask,
  TaskInbox,
} from 'core/task';
import { Section } from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { usePatientTasks } from 'hooks/useTasks';
import { TaskApi } from 'integration/aggregate';
import type { PatientDrawerScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';

const taskApi = new TaskApi();

const taskCodes: TaskCode[] = [
  TaskCode.OtherContact,
  TaskCode.OtherSchedule,
  TaskCode.ServiceRequestCreate,
  TaskCode.MedicationRequestCreate,
  TaskCode.OtherHomeVisit,
  TaskCode.OtherClinicalCallOut,
];

const PatientHeaderAssignmentButton: React.FC<CustomAction> = ({
  id,
  create,
}) => {
  const onSubmit = useCallback(
    async (result?: AssignmentResult) => {
      if (!result) {
        return;
      }

      const referenceData = getReferenceData({ reference: result.performer });

      if (!referenceData) {
        return;
      }

      await create({
        patientId: id,
        code: result.code,
        description: result.note,
        performer: createReference(
          referenceData.resourceType,
          referenceData.id,
        ),
        requestedPeriodEnd: result.dueDate
          ? result.dueDate.toString()
          : undefined,
      });
    },
    [id, create],
  );

  return (
    <WithGroup groups={[GroupSecurity.TaskCreate]}>
      <Assignment codes={taskCodes} onChange={onSubmit} patientId={id} />
    </WithGroup>
  );
};

export const PatientTasksScreen: React.FC<
  PatientDrawerScreenProps<'Tasks'>
> = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const navigation = useNavigation();
  const usePatientTaskProps = usePatientTasks({ subject: patientId });

  const navigate = useCallback<NavigateToTask>(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore TODO: Fix typing issue
    (link) => navigation.navigate(link.screen, link.params),
    [navigation],
  );

  const patientTaskListSWRKey = taskApi.getPatientSWRKey(patientId);

  return (
    <>
      <PatientHeaderSection
        id={patientId}
        action={PatientHeaderAssignmentButton}
        {...usePatientTaskProps}
      />
      <Section flex={1}>
        <TaskInbox
          navigate={navigate}
          taskListSWRKey={patientTaskListSWRKey}
          {...usePatientTaskProps}
        />
      </Section>
    </>
  );
};
